import { Component, OnInit, Input, SimpleChanges } from '@angular/core';
interface FinancialData {
   label: string;
   data: Record<number, number>;
   children?: FinancialData[];
}

interface TableComponentProps {
   _isAnnual: boolean;
   noDataAnnual: boolean;
   years: number[];
   data: FinancialData[];
   formatValue: (value: number) => string;
}

interface QuarterlyFinancialData {
   label: string;
   data: Record<string, number>; // For quarters like "Q1'23"
   children?: QuarterlyFinancialData[];
}

interface QuarterlyTableComponentProps {
   _isAnnual: boolean;
   noDataQuarterly: boolean;
   quarters: string[];
   quarterlydata: QuarterlyFinancialData[];
   formatValue: (value: number) => string;
   hasData: (data: Record<string, number>) => boolean;
}
@Component({
    selector: 'app-income-statement-table',
    templateUrl: './income-statement-table.component.html',
    styleUrls: ['./income-statement-table.component.scss'],
    standalone: false
})
export class IncomeStatementTableComponent implements OnInit {
   @Input() _isAnnual: boolean = true;
   @Input() grossMarginAnnualArray: any[] = [];

   @Input() grossMarginQuarterlyArray: any[] = [];
   @Input() data: any[] = [];
   @Input() quarterlydata: any[] = [];
   processedData: any[] = [];
   processedQuarterlyData: any[] = [];
   noDataAnnual: boolean = false;
   noDataQuarterly: boolean = false;
   filteredAnnualData: any[] = [];
   filteredQuarterlyData: any[] = [];

  years: number[] = [];
  quarters: string[] = [];

   expandedRows: Record<string, boolean> = {};

   constructor() {}

   ngOnInit(): void {
    this.processAndSetData();
    console.log("data",this.data)
   }

   ngOnChanges(changes: SimpleChanges): void {
    if (changes['quarterlydata'] && changes['quarterlydata'].currentValue) {
      //  console.log('Quarterly data changed:', changes['quarterlydata'].currentValue);
       this.processAndSetData();
    }
 }

 private processAndSetData() {
  this.processedData = this.processDataWithHierarchy(this.data);
  this.processedQuarterlyData = this.processDataWithHierarchy(this.quarterlydata);
//   console.log('Processed Quarterly Data:', this.processedQuarterlyData);

  // Extract latest years from annual data
  this.years = this.extractLatestYears(this.data);

  // Extract latest 4 quarters from quarterly data
  this.quarters = this.extractLatestQuarters(this.quarterlydata);

  this.noDataAnnual = !this.hasAnnualData(this.data);
  this.noDataQuarterly = !this.hasQuarterlyData(this.quarterlydata);
}

   private openRows: Set<string> = new Set();

   getYears(data: Record<number, number>): number[] {
      return Object.keys(data).map((year) => parseInt(year)); // Get years from the data object keys
   }

   private extractLatestYears(annualData: any[]): number[] {
      if (!annualData || annualData.length === 0) {
         return [];
      }

      // Collect all unique years from all data items
      const allYears = new Set<number>();
      annualData.forEach(item => {
         if (item.data) {
            Object.keys(item.data).forEach(year => {
               const yearNum = parseInt(year);
               if (!isNaN(yearNum)) {
                  allYears.add(yearNum);
               }
            });
         }
      });

      // Convert to array and sort years chronologically
      const sortedYears = Array.from(allYears).sort((a, b) => a - b);

      // Return the latest 5 years (last 5 from the sorted array)
      return sortedYears.slice(-5);
   }

   private extractLatestQuarters(quarterlyData: any[]): string[] {
      if (!quarterlyData || quarterlyData.length === 0) {
         return [];
      }

      // Collect all unique quarters from all data items
      const allQuarters = new Set<string>();
      quarterlyData.forEach(item => {
         if (item.data) {
            Object.keys(item.data).forEach(quarter => {
               allQuarters.add(quarter);
            });
         }
      });

      // Convert to array and sort quarters chronologically
      const sortedQuarters = Array.from(allQuarters).sort((a, b) => {
         // Parse quarter format like "Q1'25" to sort chronologically
         const parseQuarter = (q: string) => {
            const [quarter, year] = q.split("'");
            const quarterNum = parseInt(quarter.substring(1));
            const fullYear = parseInt(year) + 2000; // Convert '25 to 2025
            return fullYear * 4 + quarterNum; // Create sortable number
         };

         return parseQuarter(a) - parseQuarter(b); // Sort ascending (oldest first)
      });

      // Return the latest 4 quarters (last 4 from the sorted array)
      return sortedQuarters.slice(-4);
   }
   processDataWithHierarchy(data: FinancialData[]): FinancialData[] {
      const opExIndex = data.findIndex(item => item.label === 'totalOperatingExpense');
      const rdItem = data.find(item => item.label === 'researchDevelopment');
      const sgaItem = data.find(item => item.label === 'sgaExpense');

      if (opExIndex !== -1) {
         // Build children array dynamically to handle missing values
         const children = [];
         if (rdItem) children.push(rdItem);
         if (sgaItem) children.push(sgaItem);

         if (children.length > 0) {
            const processedData = [...data];
            processedData[opExIndex] = {
               ...data[opExIndex],
               children
            };

            // Remove only existing children from the main array
            return processedData.filter(item => item.label !== 'researchDevelopment' && item.label !== 'sgaExpense');
         }
      }

      return data;
   }


   toggleRow(item: FinancialData) {
      this.expandedRows[item.label] = !this.expandedRows[item.label];
   }

   toggleAccordion(row: string): void {
      if (this.openRows.has(row)) {
         this.openRows.delete(row); // Close the accordion
      } else {
         this.openRows.add(row); // Open the accordion
      }
   }

   isAccordionOpen(row: string): boolean {
      return this.openRows.has(row); // Check if the accordion is open
   }

   hasAnnualData(
      data: { label: string; data: Record<string, number> }[]
   ): boolean {
      return data.some((item) =>
         Object.keys(item.data).some((year) =>
            this.years.includes(Number(year))
         )
      );
   }

   hasQuarterlyData(
      quarterlyData: { label: string; data: Record<string, number> }[]
   ): boolean {
      return quarterlyData.some((item) =>
         Object.keys(item.data).some((quarter) =>
            this.quarters.includes(quarter)
         )
      );
   }

   hasData(data: any): boolean {
      return this.quarters.some(
         (q) => data[q] !== undefined && data[q] !== null
      );
   }

   formatValue = (value: number | string | undefined): string => {
      if (value === undefined || value === null || value === '') {
         return '-';
      }

      // Convert string values to numbers
      let numericValue = typeof value === 'string' ? parseFloat(value) : value;

      // Handle NaN cases (e.g., invalid strings)
      if (isNaN(numericValue)) {
         return '-';
      }

      // Multiply by 10,00,000
      numericValue *= 1_000_000;

      const absValue = Math.abs(numericValue);
      const formattedValue = (val: number) =>
         val.toFixed(2).replace(/\.?0+$/, '');

      let result: string;

      // Handle billions
      if (absValue >= 1_000_000_000) {
         result = formattedValue(absValue / 1_000_000_000) + ' B';
      }
      // Handle millions
      else if (absValue >= 1_000_000) {
         result = formattedValue(absValue / 1_000_000) + ' M';
      }
      // Handle decimal numbers
      else {
         result = formattedValue(absValue);
      }

      // Show negative values in parentheses without a negative sign
      return numericValue < 0 ? `(${result})` : result;
   };


}
